import React, { useState, useEffect, useCallback } from 'react';
import { PromptVersion, DiffResponse } from '../types/prompts';
import { fetchPromptVersions, fetchPromptDiff, restorePromptVersion, fetchPrompt } from '../utils/api';

interface VersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  promptName: string;
  onVersionRestored?: () => void;
}

const VersionHistoryModal: React.FC<VersionHistoryModalProps> = ({ 
  isOpen, 
  onClose, 
  promptName, 
  onVersionRestored 
}) => {
  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersions, setSelectedVersions] = useState<{ from: string; to: string }>({
    from: '',
    to: 'latest'
  });
  const [diffData, setDiffData] = useState<DiffResponse | null>(null);
  const [showDiff, setShowDiff] = useState(false);
  const [diffLoading, setDiffLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string>('');

  // Helper to parse timestamps like "2025-09-11_02-23-22" or generic ISO strings
  const parseTimestamp = (timestamp?: string): Date => {
    if (!timestamp) {
      return new Date(NaN);
    }

    if (timestamp.includes('_')) {
      const [datePart, timePart] = timestamp.split('_');
      const normalizedTime = timePart?.replace(/-/g, ':');
      if (datePart && normalizedTime) {
        return new Date(`${datePart}T${normalizedTime}`);
      }
    }

    return new Date(timestamp);
  };

  const formatTimestamp = (timestamp?: string, withTime: boolean = false) => {
    const date = parseTimestamp(timestamp);
    if (Number.isNaN(date.getTime())) {
      return 'Unknown date';
    }
    return withTime
      ? date.toLocaleString()
      : date.toLocaleDateString();
  };

  const loadVersions = useCallback(async () => {
    if (!promptName) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Fetch both version history and current prompt metadata
      const [versionsResponse, currentPrompt] = await Promise.all([
        fetchPromptVersions(promptName),
        fetchPrompt(promptName)
      ]);
      
      setVersions(versionsResponse.versions);
      setCurrentVersion(currentPrompt.metadata?.version || '');
      
      // Set default comparison to latest vs previous version
      if (versionsResponse.versions.length > 0) {
        setSelectedVersions({
          from: versionsResponse.versions[0]?.version || '',
          to: 'latest'
        });
      }
    } catch (err) {
      setError('Failed to load version history');
      console.error('Error loading versions:', err);
    } finally {
      setLoading(false);
    }
  }, [promptName]);

  useEffect(() => {
    if (isOpen && promptName) {
      loadVersions();
    } else {
      // Reset state when modal closes
      setVersions([]);
      setCurrentVersion('');
      setError(null);
      setDiffData(null);
      setShowDiff(false);
      setSelectedVersions({ from: '', to: 'latest' });
    }
  }, [isOpen, promptName, loadVersions]);

  const handleCompareVersions = async () => {
    if (!selectedVersions.from) return;
    
    setDiffLoading(true);
    setError(null);
    
    try {
      const diff = await fetchPromptDiff(promptName, selectedVersions.from, selectedVersions.to);
      setDiffData(diff);
      setShowDiff(true);
    } catch (err) {
      setError('Failed to generate diff');
      console.error('Error generating diff:', err);
    } finally {
      setDiffLoading(false);
    }
  };

  const handleRestoreVersion = async (version: string) => {
    if (!window.confirm(`Are you sure you want to restore version ${version}? This will create a new version with the content from ${version}.`)) {
      return;
    }
    
    setRestoring(true);
    setError(null);
    
    try {
      await restorePromptVersion(promptName, version);
      
      // Notify parent component
      if (onVersionRestored) {
        onVersionRestored();
      }
      
      // Close modal after successful restore
      onClose();
    } catch (err) {
      setError('Failed to restore version');
      console.error('Error restoring version:', err);
    } finally {
      setRestoring(false);
    }
  };

  const formatDiff = (diff: string) => {
    return diff.split('\n').map((line, index) => {
      let className = '';
      if (line.startsWith('+') && !line.startsWith('+++')) {
        className = 'diff-added';
      } else if (line.startsWith('-') && !line.startsWith('---')) {
        className = 'diff-removed';
      } else if (line.startsWith('@@')) {
        className = 'diff-header';
      }
      
      return (
        <div key={index} className={`diff-line ${className}`}>
          {line}
        </div>
      );
    });
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="prompt-modal-backdrop" onClick={handleBackdropClick}>
      <div className="prompt-modal version-history-modal">
        <div className="prompt-modal-header">
          <div className="prompt-header-info">
            <h2>Version History - {promptName}</h2>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="prompt-modal-content">
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading version history...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p>❌ {error}</p>
              <button onClick={loadVersions} className="retry-btn">
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Version List Section */}
              {!showDiff && (
                <div className="version-history-section">
                  <div className="section-header">
                    <h3>Available Versions</h3>
                    <p className="section-subtitle">
                      {versions.length} historical version{versions.length !== 1 ? 's' : ''} • Current: {currentVersion || 'latest'}
                    </p>
                  </div>

                  {/* Compare Section */}
                  <div className="compare-section">
                    <h4>Compare Versions</h4>
                    <div className="compare-controls">
                      <div className="version-select">
                        <label>From:</label>
                        <select 
                          value={selectedVersions.from}
                          onChange={(e) => setSelectedVersions({...selectedVersions, from: e.target.value})}
                        >
                          <option value="">Select version</option>
                          {versions.map(version => (
                            <option key={version.version} value={version.version}>
                              {version.version} ({formatTimestamp(version.timestamp)})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="version-select">
                        <label>To:</label>
                        <select 
                          value={selectedVersions.to}
                          onChange={(e) => setSelectedVersions({...selectedVersions, to: e.target.value})}
                        >
                          <option value="latest">Latest (Current)</option>
                          {versions.map(version => (
                            <option key={version.version} value={version.version}>
                              {version.version} ({formatTimestamp(version.timestamp)})
                            </option>
                          ))}
                        </select>
                      </div>
                      <button 
                        onClick={handleCompareVersions}
                        className="compare-btn"
                        disabled={!selectedVersions.from || diffLoading}
                      >
                        {diffLoading ? 'Comparing...' : 'Compare'}
                      </button>
                    </div>
                  </div>

                  {/* Version List */}
                  <div className="versions-list">
                    <div className="version-item current-version">
                      <div className="version-info">
                        <span className="version-name">Latest (Current)</span>
                        <span className="version-meta">Active version in pipeline</span>
                      </div>
                      <div className="version-actions">
                        <span className="current-badge">Current</span>
                      </div>
                    </div>

                    {versions.map(version => (
                      <div key={version.version} className="version-item">
                        <div className="version-info">
                          <span className="version-name">{version.version}</span>
                          <span className="version-meta">
                            {formatTimestamp(version.timestamp, true)} • {version.size} chars
                          </span>
                        </div>
                        <div className="version-actions">
                          <button 
                            onClick={() => handleRestoreVersion(version.version)}
                            className="restore-btn"
                            disabled={restoring}
                          >
                            {restoring ? 'Restoring...' : 'Restore'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Diff Display Section */}
              {showDiff && diffData && (
                <div className="diff-section">
                  <div className="section-header">
                    <h3>Version Comparison</h3>
                    <p className="section-subtitle">
                      {diffData.from_version} → {diffData.to_version} • 
                      <span className="diff-stats">
                        +{diffData.added_lines} -{diffData.removed_lines} lines
                      </span>
                    </p>
                    <button 
                      onClick={() => setShowDiff(false)}
                      className="back-btn"
                    >
                      ← Back to Versions
                    </button>
                  </div>

                  <div className="diff-display">
                    {diffData.diff ? (
                      <pre className="diff-content">
                        {formatDiff(diffData.diff)}
                      </pre>
                    ) : (
                      <div className="no-changes">
                        <p>No differences found between these versions.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VersionHistoryModal;
